import { useQuery } from '@tanstack/react-query'
import { Badge, Button, InfoBox, LoadingState, kstMonthDayStr, kstTimeStr } from '@biznomi/ui-react'
import { verifyChain } from '@/lib/api/admin'

/**
 * 체인 검증 — "이 기록이 조작되지 않았음"을 보여주는 화면.
 *
 * 이 제품이 파는 것이 이거다. 그래서 결과를 **정확하게** 말해야 한다.
 * 특히 "체인 끊김"은 변조와 다르다 —
 *   변조    = 앱을 거치지 않고 DB 가 직접 바뀜.  진짜 문제
 *   승인수정 = 관리자가 앱에서 고침. 감사로그가 설명함. 문제 아님
 *   끊김    = 삭제 등으로 연결이 빠짐. 데이터 문제이지 변조가 아님
 * 셋을 뭉뚱그리면 병원 담당자는 "우리 데이터가 조작됐다" 로 읽는다.
 *
 * ⓘ 초록불의 한계를 이 화면에 적지 않기로 했다 (2026-09-02).
 *   이 검사가 잡는 건 체인을 모르고 DB 를 고친 경우뿐이고, 고친 뒤 해시까지 다시
 *   계산하면 통과한다. 사실이지만 **고객사 관리자가 매일 보는 화면에 적을 말은
 *   아니다.** 그 말이 필요한 자리는 영업 대본이고 거기 적어 뒀다.
 *   한계를 없애는 방법은 문구가 아니라 외부 원장 anchoring 이다 (CLAUDE.md P1).
 *   그게 붙으면 이 자리에 "N월 N일까지 외부에 고정됨" 을 더한다.
 */

function Stat({ label, value, hint, tone }: { label: string; value: number | string; hint?: string; tone?: 'warn' | 'danger' }) {
  return (
    <div className="rounded-card bg-card p-4">
      <p className="text-sm text-slate-500">{label}</p>
      <p
        className={
          'mt-1 text-2xl font-bold tabular-nums ' +
          (tone === 'danger' ? 'text-[var(--color-danger)]' : tone === 'warn' ? 'text-[var(--color-warning)]' : '')
        }
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  )
}

export default function ChainVerify() {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'chain-verify'],
    queryFn: async () => {
      const res = await verifyChain()
      if (!res.success || !res.data) throw new Error(res.error ?? '검증에 실패했습니다')
      return res.data
    },
    // 전 기록을 훑는 무거운 조회다. 자동 갱신하지 않는다
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  if (isLoading) return <LoadingState label="기록을 하나씩 다시 계산하는 중…" />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>
  if (!data) return null

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold">체인 검증</h1>
          <p className="mt-1 text-sm text-slate-500">
            저장된 기록을 하나씩 다시 계산해 원본과 대조합니다
          </p>
        </div>
        <Button size="sm" variant="secondary" loading={isFetching} onClick={() => refetch()}>
          다시 검증
        </Button>
      </header>

      {/*
       * 결론을 먼저. 다만 "변조"와 "끊김"을 구분해 말한다.
       *
       * ⚠️ 검증 대상이 0건이면 초록을 띄우지 않는다. 서버는 "어긋난 게 없다"는
       *    뜻으로 ok=true 를 주지만, 검사한 게 없어서 어긋난 것도 없는 상태다.
       *    그걸 "위변조 흔적 없음"으로 보여주면 우리가 파는 신뢰를 우리가 깎는다.
       */}
      {data.chainedRows === 0 ? (
        <InfoBox tone="warning" title="아직 검증할 수 있는 기록이 없습니다">
          검증은 저장할 때 계산해 둔 값이 있어야 가능합니다. 지금은 {data.totalRows.toLocaleString()}건 중
          그 값을 가진 기록이 없어, 위변조 여부를 판단할 수 없습니다. 위변조가 없다는 뜻이 아닙니다.
        </InfoBox>
      ) : data.ok ? (
        <InfoBox tone="success" title="위변조 흔적이 없습니다">
          {data.chainedRows.toLocaleString()}건을 다시 계산해 전부 원본과 일치했고, 기록이 처음부터 끝까지
          하나로 이어져 있습니다.
        </InfoBox>
      ) : data.tamperedCount > 0 ? (
        <InfoBox tone="danger" title={`변조 흔적 ${data.tamperedCount}건`}>
          앱을 거치지 않고 값이 바뀐 기록이 있습니다. 관리자가 앱에서 고친 것은 감사 로그로 설명되므로 여기
          포함되지 않습니다. 아래 목록의 시각을 감사 로그와 대조해 보세요.
        </InfoBox>
      ) : (
        <InfoBox tone="warning" title="이어지지 않는 구간이 있습니다">
          값이 바뀐 기록은 <b>없습니다</b>. 다만 기록이 한 줄로 이어지지 않는데, 보통 중간 기록이 삭제됐을
          때 이렇게 됩니다. 변조와는 다른 상태입니다.
        </InfoBox>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="전체 기록" value={data.totalRows.toLocaleString()} />
        <Stat label="검증 대상" value={data.chainedRows.toLocaleString()} hint="해시가 있는 기록" />
        <Stat
          label="이어진 기록"
          value={data.walkedRows.toLocaleString()}
          hint={data.chainBroken ? '중간이 끊김' : '처음부터 끝까지'}
          tone={data.chainBroken ? 'warn' : undefined}
        />
        <Stat
          label="변조 흔적"
          value={data.tamperedCount}
          hint="앱을 거치지 않은 변경"
          tone={data.tamperedCount > 0 ? 'danger' : undefined}
        />
      </div>

      {(data.approvedEditCount > 0 || data.fallbackCount > 0) && (
        <div className="grid gap-3 md:grid-cols-2">
          {data.approvedEditCount > 0 && (
            <Stat
              label="관리자 승인 수정"
              value={data.approvedEditCount}
              hint="감사 로그로 설명되는 정상 수정"
            />
          )}
          {data.fallbackCount > 0 && (
            <Stat
              label="체인 밖 기록"
              value={data.fallbackCount.toLocaleString()}
              /* 계산 실패(CHAIN_FAILED)일 수도, 체인 도입 전 기록일 수도 있다.
                 서버는 둘 다 hash IS NULL 로만 세므로 원인을 단정하지 않는다 */
              hint="해시가 없어서 검증은 못 하지만, 태깅은 정상으로 저장됐습니다"
              tone="warn"
            />
          )}
        </div>
      )}

      {data.tamperedRows.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-bold text-slate-500">
            변조 흔적 {data.tamperedRows.length > 20 ? '(처음 20건)' : ''}
          </h2>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {data.tamperedRows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="font-mono text-xs text-slate-400">{r.id.slice(0, 8)}…</span>
                <span className="text-sm">
                  {kstMonthDayStr(r.createdAt)} {kstTimeStr(r.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.approvedEditRows.length > 0 && (
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-500">
            관리자 승인 수정 <Badge variant="info">변조 아님</Badge>
          </h2>
          <ul className="divide-y divide-slate-100 overflow-hidden rounded-card bg-card">
            {data.approvedEditRows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="font-mono text-xs text-slate-400">{r.id.slice(0, 8)}…</span>
                <span className="text-sm">
                  {kstMonthDayStr(r.createdAt)} {kstTimeStr(r.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-xs leading-relaxed text-slate-400">
        각 기록은 저장될 때 앞 기록의 값을 함께 계산해 넣습니다. 하나를 고치면 그 뒤가 전부 어긋나기
        때문에, 나중에 몰래 바꾸면 여기서 드러납니다.
      </p>
    </div>
  )
}
